import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Autocomplete, AutocompleteItem, Button, Divider, Input, Select, SelectItem, Switch } from "@nextui-org/react";
import {
  setTenantMetadata,
  useAppDispatch,
  useTenantDetails,
  useGetTenantTimezonesQuery,
  useUpdateTenantDetailsMutation,
  useGetTenantMetadataPresignedUrlMutation,
  useUploadFileToS3Mutation,
} from "@circuitry-ai/doc-data";
import { CtyButton, CtyReactIcon, CtyFileUploader, CtySpinner, ErrorBoundary, CtyShowToast, checkType } from "@circuitry-ai/doc-ui";
import { useTranslation } from 'react-i18next';
import { OrganizationDetails } from "modules/shared/data/src/features/settings/settingstypes";

const getFileNameFromUrl = (url: string): string => {
  if (!url) return '';
  try {
    const pathname = new URL(url).pathname;
    const name = pathname.split('/').pop();
    return name ? decodeURIComponent(name) : url;
  } catch {
    const name = url.split('/').pop()?.split('?')[0];
    return name ? decodeURIComponent(name) : url;
  }
};

const isCssFile = (file: File): boolean =>
  file.type === 'text/css' || file.name.toLowerCase().endsWith('.css');

/** Matches PAL `bg-primary` / `--cty-primary` when `pal_header_bg` is unset. */
const PAL_HEADER_BG_FALLBACK = '#003a79';
/** Matches PAL `text-white` when `pal_text_color` is unset. */
const PAL_HEADER_TEXT_FALLBACK = '#ffffff';

const toHexColor = (value: unknown, fallback: string): string => {
  const hex = typeof value === 'string' ? value.trim() : '';
  return /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : fallback;
};

export default function ProfileSetting() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    tenantDetails,
    error: userDetailsError,
    isLoading: userDetailsLoading,
    isFetching: isTenantDetailsFetching,
  } = useTenantDetails({ refetchOnMountOrArgChange: true });
  let orgName = ''
  if (tenantDetails && 'org_name' in tenantDetails) {
    orgName = (tenantDetails as OrganizationDetails).org_name;
  }

  const ProfileContent = ({
    t,
    orgName,
    tenantDetails,
    isTenantDetailsFetching
  }: {
    t: any;
    orgName: string;
    tenantDetails?: OrganizationDetails;
    isTenantDetailsFetching: boolean;
  }) => {
    const [chatWidgetFile, setChatWidgetFile] = useState<File | null>(null);
    const [chatWidgetPreview, setChatWidgetPreview] = useState<string>('');
    const [uploadedWidgetIconUrl, setUploadedWidgetIconUrl] = useState<string>('');
    const [isFileLoading, setIsFileLoading] = useState(false);
    const [widgetIconRemovedByUser, setWidgetIconRemovedByUser] = useState(false);
    const [cssFilePreview, setCssFilePreview] = useState('');
    const [cssFileLabel, setCssFileLabel] = useState('');
    const [uploadedCssFileUrl, setUploadedCssFileUrl] = useState('');
    const [isCssFileLoading, setIsCssFileLoading] = useState(false);
    const [cssFileRemovedByUser, setCssFileRemovedByUser] = useState(false);
    const [brandFontUrl, setBrandFontUrl] = useState('');
    const [fontColor, setFontColor] = useState('');
    const [palHeaderBg, setPalHeaderBg] = useState(PAL_HEADER_BG_FALLBACK);
    const [palHeaderTextColor, setPalHeaderTextColor] = useState(PAL_HEADER_TEXT_FALLBACK);
    const [enableRoles, setEnableRoles] = useState(false);
    const [timezone, setTimezone] = useState('');
    const [dateFormat, setDateFormat] = useState('');
    const [dateTimeFormat, setDateTimeFormat] = useState('');
    const [stylingTarget, setStylingTarget] = useState('application');
    const chatWidgetInputRef = useRef<HTMLInputElement>(null);
    const cssFileInputRef = useRef<HTMLInputElement>(null);
    const uploadInProgressRef = useRef(false);
    const cssUploadInProgressRef = useRef(false);
    const dateFormatOptions = useMemo(
      () => ['', 'MM-dd-yyyy', 'dd-MM-yyyy', 'yyyy-MM-dd', 'MM/dd/yyyy', 'MMM-dd-yyyy', 'dd-MMM-yyyy'],
      []
    );
    const dateTimeFormatOptions = useMemo(
      () => ['', 'MM-dd-yyyy HH:mm:ss', 'dd-MM-yyyy HH:mm:ss', 'yyyy-MM-dd HH:mm:ss', 'MM/dd/yyyy HH:mm:ss', 'dd-MMM-yyyy HH:mm:ss', 'MMM-dd-yyyy HH:mm:ss'],
      []
    );

    const {
      data: timezoneOptions = [],
      isLoading: isTimezonesLoading,
      isFetching: isTimezonesFetching,
    } = useGetTenantTimezonesQuery();
    const [updateTenantDetails, { isLoading: isSaving }] = useUpdateTenantDetailsMutation();
    const [getTenantMetadataPresignedUrl] = useGetTenantMetadataPresignedUrlMutation();
    const [uploadFileToS3] = useUploadFileToS3Mutation();
    const existingMetaData = (tenantDetails?.meta_data ?? {}) as Record<string, unknown>;
    const existingWidgetIconUrl = (existingMetaData.widget_icon_url as string) ?? '';
    const existingCssFileUrl = (existingMetaData.css_url as string) ?? '';

    useEffect(() => {
      // Prevent field flicker from stale cached data while a fresh tenant fetch is in flight.
      if (!tenantDetails || isTenantDetailsFetching) return;
      setBrandFontUrl((existingMetaData.font_url as string) ?? '');
      setFontColor((existingMetaData.font_color as string) ?? '#002E61');
      setPalHeaderBg(toHexColor(existingMetaData.pal_header_bg, PAL_HEADER_BG_FALLBACK));
      setPalHeaderTextColor(toHexColor(existingMetaData.pal_text_color, PAL_HEADER_TEXT_FALLBACK));
      setEnableRoles(existingMetaData.enable_roles === true);
      setTimezone(tenantDetails.default_timezone ?? '');
      setDateFormat(((existingMetaData.preference as Record<string, unknown> | undefined)?.dateFormat as string) ?? '');
      setDateTimeFormat(((existingMetaData.preference as Record<string, unknown> | undefined)?.dateTimeFormat as string) ?? '');
    }, [tenantDetails, existingMetaData.font_url, existingMetaData.font_color, existingMetaData.pal_header_bg, existingMetaData.pal_text_color, existingMetaData.enable_roles, isTenantDetailsFetching]);

    useEffect(() => {
      if (!tenantDetails || isTenantDetailsFetching) return;
      if (!widgetIconRemovedByUser && !chatWidgetFile && !uploadedWidgetIconUrl && existingMetaData.widget_icon_url) {
        setChatWidgetPreview((existingMetaData.widget_icon_url as string) ?? '');
      }
    }, [tenantDetails, existingMetaData.widget_icon_url, chatWidgetFile, uploadedWidgetIconUrl, widgetIconRemovedByUser, isTenantDetailsFetching]);

    useEffect(() => {
      if (!tenantDetails || isTenantDetailsFetching) return;
      if (!cssFileRemovedByUser && !uploadedCssFileUrl && existingMetaData.css_url) {
        const cssUrl = (existingMetaData.css_url as string) ?? '';
        setCssFilePreview(cssUrl);
        setCssFileLabel(getFileNameFromUrl(cssUrl));
      }
    }, [tenantDetails, existingMetaData.css_url, uploadedCssFileUrl, cssFileRemovedByUser, isTenantDetailsFetching]);

    const handleSave = useCallback(async () => {
      const mergedMetadata: Record<string, unknown> = {
        ...existingMetaData,
        font_url: brandFontUrl || '',
        font_color: fontColor || '',
        pal_header_bg: palHeaderBg || '',
        pal_text_color: palHeaderTextColor || '',
        enable_roles: enableRoles,
        preference: {
          ...((existingMetaData.preference as Record<string, unknown>) ?? {}),
          dateFormat: dateFormat || '',
          dateTimeFormat: dateTimeFormat || '',
        },
       category : stylingTarget
      };

      if (uploadedWidgetIconUrl) {
        mergedMetadata.widget_icon_url = uploadedWidgetIconUrl;
      } else if (widgetIconRemovedByUser || ((existingMetaData.widget_icon_url as string) && !chatWidgetPreview)) {
        mergedMetadata.widget_icon_url = '';
      }

      if (uploadedCssFileUrl) {
        mergedMetadata.css_url = uploadedCssFileUrl;
      } else if (cssFileRemovedByUser || ((existingMetaData.css_url as string) && !cssFilePreview)) {
        mergedMetadata.css_url = '';
      }

      try {
        const response = await updateTenantDetails({
          meta_data: mergedMetadata,
          default_timezone: timezone || '',
        }).unwrap();
        dispatch(setTenantMetadata({ meta_data: mergedMetadata, default_timezone: timezone || '' }));
        console.log('response', response);
        CtyShowToast(checkType.success, t('Changes saved successfully'));
        setWidgetIconRemovedByUser(false);
        setCssFileRemovedByUser(false);
      } catch {
        CtyShowToast(checkType.error, t('Failed to save changes'));
      }
    }, [dispatch, existingMetaData, tenantDetails, brandFontUrl, fontColor, palHeaderBg, palHeaderTextColor, enableRoles, timezone, dateFormat, dateTimeFormat, uploadedWidgetIconUrl, chatWidgetPreview, uploadedCssFileUrl, cssFilePreview, updateTenantDetails, widgetIconRemovedByUser, cssFileRemovedByUser, t, stylingTarget]);

    const handleCancel = useCallback(() => {
      if (tenantDetails) {
        setBrandFontUrl((existingMetaData.font_url as string) || '');
        setFontColor((existingMetaData.font_color as string) || '#002E61');
        setPalHeaderBg(toHexColor(existingMetaData.pal_header_bg, PAL_HEADER_BG_FALLBACK));
        setPalHeaderTextColor(toHexColor(existingMetaData.pal_text_color, PAL_HEADER_TEXT_FALLBACK));
        setEnableRoles(existingMetaData.enable_roles === true);
        setTimezone(tenantDetails.default_timezone || '');
        setDateFormat(((existingMetaData.preference as Record<string, unknown> | undefined)?.dateFormat as string) || '');
        setDateTimeFormat(((existingMetaData.preference as Record<string, unknown> | undefined)?.dateTimeFormat as string) || '');
        setWidgetIconRemovedByUser(false);
        setCssFileRemovedByUser(false);
        if (existingMetaData.widget_icon_url && !chatWidgetFile && !uploadedWidgetIconUrl) {
          setChatWidgetPreview((existingMetaData.widget_icon_url as string) || '');
        }
        if (existingMetaData.css_url && !uploadedCssFileUrl) {
          const cssUrl = (existingMetaData.css_url as string) || '';
          setCssFilePreview(cssUrl);
          setCssFileLabel(getFileNameFromUrl(cssUrl));
        }
      }
      setChatWidgetFile(null);
      setUploadedWidgetIconUrl('');
      setChatWidgetPreview(existingWidgetIconUrl);
      setUploadedCssFileUrl('');
      setCssFilePreview(existingCssFileUrl);
      setCssFileLabel(getFileNameFromUrl(existingCssFileUrl));
      if (chatWidgetInputRef.current) chatWidgetInputRef.current.value = '';
      if (cssFileInputRef.current) cssFileInputRef.current.value = '';
    }, [existingMetaData, existingWidgetIconUrl, existingCssFileUrl, tenantDetails, chatWidgetFile, uploadedWidgetIconUrl, uploadedCssFileUrl]);

    const mergedTimezoneOptions = (() => {
      if (!timezone) return timezoneOptions;
      const hasTimezone = timezoneOptions.some((option) => option.name === timezone);
      return hasTimezone ? timezoneOptions : [{ name: timezone, abbrev: '' }, ...timezoneOptions];
    })();
    const isTimezoneAutocompleteLoading =
      userDetailsLoading || isTenantDetailsFetching || isTimezonesLoading || isTimezonesFetching;

    const  fontColorStyles = `
      .color-picker {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        width: 40px;
        height: 38px;
        border: none;
        border-radius: 50%;
        padding: 0;
        cursor: pointer;
      }

      /* Remove inner padding in Chrome */
      .color-picker::-webkit-color-swatch-wrapper {
        padding: 0;
      }

      /* Make actual color circle */
      .color-picker::-webkit-color-swatch {
        border: none;
        border-radius: 50%;
      }

      /* Firefox */
      .color-picker::-moz-color-swatch {
        border: none;
        border-radius: 50%;
      }
    `;
    const handleWidgetFiles = useCallback(async (files: FileList | null) => {
      const file = files?.[0];
      if (!file || !file.type.startsWith('image/')) return;
      if (uploadInProgressRef.current) return;
      uploadInProgressRef.current = true;
      if (chatWidgetInputRef.current) chatWidgetInputRef.current.value = '';
      setIsFileLoading(true);
      setWidgetIconRemovedByUser(false);
      try {
        const { presigned_url, widget_icon_url } = await getTenantMetadataPresignedUrl({ filename: file.name }).unwrap();
        if (!presigned_url) {
          CtyShowToast(checkType.error, t('Failed to get upload URL'));
          setChatWidgetPreview(existingWidgetIconUrl);
          uploadInProgressRef.current = false;
          setIsFileLoading(false);
          return;
        }
        await uploadFileToS3({ file, presignedUrl: presigned_url, CtyShowToast }).unwrap();
        setUploadedWidgetIconUrl(widget_icon_url || '');
        setChatWidgetPreview(widget_icon_url || '');
        setChatWidgetFile(null);
      } catch (error) {
        CtyShowToast(checkType.error, (error as Error)?.message ?? t('Error uploading file'));
        setChatWidgetPreview(existingWidgetIconUrl);
      } finally {
        uploadInProgressRef.current = false;
        setIsFileLoading(false);
        if (chatWidgetInputRef.current) chatWidgetInputRef.current.value = '';
      }
    }, [existingWidgetIconUrl, getTenantMetadataPresignedUrl, uploadFileToS3, t]);

    const handleCssFiles = useCallback(async (files: FileList | null) => {
      const file = files?.[0];
      if (!file || !isCssFile(file)) {
        CtyShowToast(checkType.error, t('Please upload a valid CSS file'));
        return;
      }
      if (file.size > 1024 * 1024) {
        CtyShowToast(checkType.error, t('CSS file must be 1MB or smaller'));
        return;
      }
      if (cssUploadInProgressRef.current) return;
      cssUploadInProgressRef.current = true;
      if (cssFileInputRef.current) cssFileInputRef.current.value = '';
      setIsCssFileLoading(true);
      setCssFileRemovedByUser(false);
      try {
        const { presigned_url, widget_icon_url } = await getTenantMetadataPresignedUrl({ filename: file.name }).unwrap();
        if (!presigned_url) {
          CtyShowToast(checkType.error, t('Failed to get upload URL'));
          setCssFilePreview(existingCssFileUrl);
          setCssFileLabel(getFileNameFromUrl(existingCssFileUrl));
          cssUploadInProgressRef.current = false;
          setIsCssFileLoading(false);
          return;
        }
        await uploadFileToS3({ file, presignedUrl: presigned_url, CtyShowToast }).unwrap();
        const cssUrl = widget_icon_url || '';
        setUploadedCssFileUrl(cssUrl);
        setCssFilePreview(cssUrl);
        setCssFileLabel(file.name);
      } catch (error) {
        CtyShowToast(checkType.error, (error as Error)?.message ?? t('Error uploading file'));
        setCssFilePreview(existingCssFileUrl);
        setCssFileLabel(getFileNameFromUrl(existingCssFileUrl));
      } finally {
        cssUploadInProgressRef.current = false;
        setIsCssFileLoading(false);
        if (cssFileInputRef.current) cssFileInputRef.current.value = '';
      }
    }, [existingCssFileUrl, getTenantMetadataPresignedUrl, uploadFileToS3, t]);

    const onCssFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      handleCssFiles(e.dataTransfer.files);
    }, [handleCssFiles]);

    const onCssFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      handleCssFiles(e.target.files);
    }, [handleCssFiles]);

    const clearCssFile = useCallback(() => {
      setCssFilePreview('');
      setCssFileLabel('');
      setUploadedCssFileUrl('');
      setCssFileRemovedByUser(true);
      if (cssFileInputRef.current) cssFileInputRef.current.value = '';
    }, []);

    const onChatWidgetDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      handleWidgetFiles(e.dataTransfer.files);
    }, [handleWidgetFiles]);

    const onChatWidgetChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      handleWidgetFiles(e.target.files);
    }, [handleWidgetFiles]);

    const clearChatWidget = useCallback(() => {
      setChatWidgetPreview('');
      setChatWidgetFile(null);
      setUploadedWidgetIconUrl('');
      setWidgetIconRemovedByUser(true);
      if (chatWidgetInputRef.current) chatWidgetInputRef.current.value = '';
    }, []);


const handleCSSDownload = async() => {
  try {
   const res = await fetch(cssFilePreview)
   const cssContent=await res.text()
   const blob=new Blob([cssContent], { type: 'text/css' })
   const url=URL.createObjectURL(blob)
   const a=document.createElement('a')
   a.href=url
   a.download = cssFileLabel || getFileNameFromUrl(cssFilePreview)
   a.click()
   URL.revokeObjectURL(url)
   CtyShowToast(checkType.success, 'CSS file downloaded successfully');
  } catch (error) {
    CtyShowToast(checkType.error, (error as Error)?.message ?? 'Failed to download CSS file');
  }
}

    return (
      <div className="w-full max-w-3xl">
        <style>{fontColorStyles}</style>
        <div className="pt-4">
          <div>
            <div>
              <label className="text-sm font-medium text-foreground">
                {t('Company Name')} <span className="text-danger">*</span>
              </label>
            </div>
            <div>
              <Input
                data-testid="company-name"
                size="md"
                startContent={!orgName && <CtySpinner size="sm" />}
                variant="bordered"
                className="mt-1"
                classNames={{ inputWrapper: 'bg-content1 border-default-200' }}
                value={orgName}
                endContent={
                  <CtyReactIcon
                    icon="mingcute:question-line"
                    width="16"
                    height="16"
                  />
                }
                isDisabled
              />
              <p className="mt-1 text-sm font-normal text-default-500">
                {t("This will be displayed in your Advisor's chat interface")}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <div>
              <label className="text-sm font-medium text-foreground">{t('Company URL')}</label>
            </div>
            <div>
              <Input
                size="md"
                value={orgName + '.circuitry.ai'}
                variant="bordered"
                className="mt-1"
                classNames={{ inputWrapper: 'bg-content1 border-default-200' }}
                endContent={
                  <CtyReactIcon
                    icon="mingcute:question-line"
                    width="16"
                    height="16"
                  />
                }
                isDisabled
              />
            </div>

            <div className="mt-4">
                <label className="block text-sm font-medium text-foreground mb-2">{t('Time Zone')}</label>
                <Autocomplete
                  id="company-profile-timezone"
                  aria-label="company-profile-timezone"
                  size="md"
                  variant="bordered"
                  placeholder={isTimezoneAutocompleteLoading ? t('Loading timezones...') : t('Select time zone')}
                  classNames={{ base: 'max-w-full', selectorButton: 'text-default-400' }}
                  selectedKey={timezone || null}
                  onSelectionChange={(key) => setTimezone(key ? String(key) : '')}
                  isLoading={isTimezoneAutocompleteLoading}
                >
                  {mergedTimezoneOptions.map((option) => (
                    <AutocompleteItem key={option.name} textValue={`${option.name} (${option.abbrev || 'GMT'})`}>
                      {`${option.name} (${option.abbrev || 'GMT'})`}
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground mb-2">{t('Date Format')}</label>
                <Select
                  id="company-profile-date-format"
                  aria-label="company-profile-date-format"
                  size="md"
                  variant="bordered"
                  selectedKeys={dateFormat ? new Set([dateFormat]) : new Set(['__empty__'])}
                  onSelectionChange={(keys) => {
                    const selectedKey = Array.from(keys as Set<React.Key>)[0];
                    setDateFormat(selectedKey === '__empty__' ? '' : String(selectedKey || ''));
                  }}
                  classNames={{ trigger: 'bg-content1 border-default-200' }}
                  placeholder={t('Select date format')}
                >
                  {dateFormatOptions.map((format) => (
                    <SelectItem key={format || '__empty__'} value={format}>
                      {format || ' '}
                    </SelectItem>
                  ))}
                </Select>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground mb-2">{t('Date Time Format')}</label>
                <Select
                  id="company-profile-date-time-format"
                  aria-label="company-profile-date-time-format"
                  size="md"
                  variant="bordered"
                  selectedKeys={dateTimeFormat ? new Set([dateTimeFormat]) : new Set(['__empty__'])}
                  onSelectionChange={(keys) => {
                    const selectedKey = Array.from(keys as Set<React.Key>)[0];
                    setDateTimeFormat(selectedKey === '__empty__' ? '' : String(selectedKey || ''));
                  }}
                  classNames={{ trigger: 'bg-content1 border-default-200' }}
                  placeholder={t('Select date time format')}
                >
                  {dateTimeFormatOptions.map((format) => (
                    <SelectItem key={format || '__empty__'} value={format}>
                      {format || ' '}
                    </SelectItem>
                  ))}
                </Select>
              </div>

            <Divider className="my-3" />

            {/* Chat interface customization */}
            <div className="mt-3">
              <h2 className="text-base font-semibold text-foreground">{t('Chat interface customization')}</h2>
              <p className="text-sm text-default-500 mt-0.5">{t("Update the look and feel of your Advisor's chat interface.")}</p>

              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground mb-2">{t('Chat Widget Icon')}</label>
                <CtyFileUploader
                  inputRef={chatWidgetInputRef}
                  uploadedImageUrl={chatWidgetPreview}
                  fileLoading={isFileLoading}
                  subText={t('SVG, PNG, JPG (max. 800x800px)')}
                  handleDrop={onChatWidgetDrop}
                  handleFileInputChange={onChatWidgetChange}
                />
                {chatWidgetPreview && (
                  <div className="flex justify-end pt-2">
                    <CtyButton id="chat-widget-remove-btn" color="danger" variant="light" onPress={clearChatWidget}>
                      {t('Remove')}
                    </CtyButton>
                  </div>
                )}
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground mb-2">{t('Brand Font URL')} ({t('Optional')})</label>
                <Input
                  size="md"
                  variant="bordered"
                  placeholder="https://fonts.google.com/specimen/Montserrat"
                  value={brandFontUrl}
                  onValueChange={setBrandFontUrl}
                  classNames={{ inputWrapper: 'bg-content1 border-default-200' }}
                />
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground">{t('Text Color')}</label>
                <p className="text-sm text-default-500 mb-2">{t('Select the text color')}</p>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={fontColor}
                    onChange={(e) => setFontColor(e.target.value)}
                    className="w-10 h-10 rounded-lg border border-default-200 cursor-pointer bg-transparent p-0.5 color-picker"
                    aria-label={t('Select the text color')}
                  />
                  <Input
                    size="md"
                    variant="bordered"
                    value={fontColor}
                    onValueChange={setFontColor}
                    placeholder="#7F56D9"
                    classNames={{ inputWrapper: 'bg-content1 border-default-200 max-w-[200px]' }}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground">{t('PAL header background color')}</label>
                <p className="text-sm text-default-500 mb-2">{t('Select the PAL header background color')}</p>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={toHexColor(palHeaderBg, PAL_HEADER_BG_FALLBACK)}
                    onChange={(e) => setPalHeaderBg(e.target.value)}
                    className="w-10 h-10 rounded-lg border border-default-200 cursor-pointer bg-transparent p-0.5 color-picker"
                    aria-label={t('Select the PAL header background color')}
                  />
                  <Input
                    size="md"
                    variant="bordered"
                    value={palHeaderBg}
                    onValueChange={setPalHeaderBg}
                    placeholder={PAL_HEADER_BG_FALLBACK}
                    classNames={{ inputWrapper: 'bg-content1 border-default-200 max-w-[200px]' }}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground">{t('PAL header text color')}</label>
                <p className="text-sm text-default-500 mb-2">{t('Select the PAL header text color')}</p>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={toHexColor(palHeaderTextColor, PAL_HEADER_TEXT_FALLBACK)}
                    onChange={(e) => setPalHeaderTextColor(e.target.value)}
                    className="w-10 h-10 rounded-lg border border-default-200 cursor-pointer bg-transparent p-0.5 color-picker"
                    aria-label={t('Select the PAL header text color')}
                  />
                  <Input
                    size="md"
                    variant="bordered"
                    value={palHeaderTextColor}
                    onValueChange={setPalHeaderTextColor}
                    placeholder={PAL_HEADER_TEXT_FALLBACK}
                    classNames={{ inputWrapper: 'bg-content1 border-default-200 max-w-[200px]' }}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-foreground mb-2">{t('CSS File')}</label>
                <Select
                 label='Category'
                 size="sm" 
                 variant="bordered" 
                 selectedKeys={stylingTarget ? new Set([stylingTarget]) : new Set(['__empty__'])} 
                 onSelectionChange={(keys) => {
                  const selectedKey = Array.from(keys as Set<React.Key>)[0];
                  setStylingTarget(selectedKey === '__empty__' ? '' : String(selectedKey || ''));
                }} 
                classNames={{ trigger: 'bg-content1 border-default-200 w-1/2' }}
                >
                  <SelectItem key="application" value="application">Application</SelectItem>
                  <SelectItem key="dita" value="dita">DITA</SelectItem>
                </Select>
                <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-default-500">Upload a custom CSS file for the chat interface</p>
                {cssFilePreview && 
                <Button 
                size="sm" 
                variant="bordered" 
                color="primary" 
                onClick={handleCSSDownload} 
                title="Download" 
                endContent={<CtyReactIcon icon="mingcute:download-line" width="20" height="20"/>}
                isIconOnly
                />
                }
                </div>
                <CtyFileUploader
                  inputRef={cssFileInputRef}
                  uploadedImageUrl={cssFilePreview}
                  uploadedFileLabel={cssFileLabel || getFileNameFromUrl(cssFilePreview)}
                  previewType="file"
                  accept=".css,text/css"
                  fileLoading={isCssFileLoading}
                  subText={t('CSS only (max. 1MB)')}
                  handleDrop={onCssFileDrop}
                  handleFileInputChange={onCssFileChange}
                />
                {cssFilePreview && (
                  <div className="flex justify-end pt-2">
                    <CtyButton id="css-file-remove-btn" color="danger" variant="light" onPress={clearCssFile}>
                      {t('Remove')}
                    </CtyButton>
                  </div>
                )}
              </div>

              <div className="mt-4 flex items-start justify-between gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground">{t('Enable Roles')}</label>
                  <p className="text-sm text-default-500 mt-1">{t('Enable Roles in sidebar navigation')}</p>
                </div>
                <Switch
                  id="company-profile-enable-roles"
                  size="sm"
                  color="success"
                  isSelected={enableRoles}
                  onValueChange={setEnableRoles}
                  aria-label={t('Enable Roles in sidebar navigation')}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2 sm:justify-end my-4">
          <Button
            id="cancel-button"
            className="max-sm:flex-1"
            variant="flat"
            size="sm"
            onPress={handleCancel}
          >
            {t('Cancel')}
          </Button>
          <Button
            id="save-button"
            className="max-sm:flex-1"
            color="primary"
            size="sm"
            isLoading={isSaving}
            isDisabled={isSaving}
            onPress={handleSave}
          >
            {t('Save Changes')}
          </Button>
        </div>
      </div>
    );
  };

  const ProfileContentWithLoadingAndError = ErrorBoundary(ProfileContent);

  return (
    <div className="flex flex-col h-full">
      <header className="flex flex-col gap-1 mt-5">
        <h1 className="font-poppins text-2xl font-semibold leading-8 text-foreground">{t('Company Profile')}</h1>
        <p className="font-poppins text-sm font-normal leading-5 text-default-500">{t('Configure company name and workspace URL')}</p>
      </header>
      <ProfileContentWithLoadingAndError
        error={userDetailsError}
        isLoading={userDetailsLoading}
        t={t}
        orgName={orgName}
        tenantDetails={tenantDetails}
        isTenantDetailsFetching={isTenantDetailsFetching}
      />
    </div>
  );

}